import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Hotel, ArrowRight, Check, CalendarCheck, CreditCard, FileDown, ShieldCheck, BadgeCheck, Clock, XCircle,
} from 'lucide-react';

/**
 * Marketing-Abschnitt für das Produkt "Hotel-Reservierung für das Visa-Dossier".
 * Steht auf der Landingpage direkt vor dem Pricing.
 */
const HotelPromoSection = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleOrder = () => navigate(isAuthenticated ? '/dashboard#hotel-booking' : '/register');
  const scrollToPricing = () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });

  const benefits = [t('hotel_promo_benefit_1'), t('hotel_promo_benefit_2'), t('hotel_promo_benefit_3'), t('hotel_promo_benefit_4')];

  const steps = [
    { icon: CreditCard, title: t('hotel_step_1_title'), desc: t('hotel_step_1_desc') },
    { icon: Hotel, title: t('hotel_step_2_title'), desc: t('hotel_step_2_desc') },
    { icon: FileDown, title: t('hotel_step_3_title'), desc: t('hotel_step_3_desc') },
  ];

  return (
    <section id="hotel-reservation" className="py-16 md:py-24 bg-white" data-testid="hotel-promo-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-[#FF3B30]/10 text-[#FF3B30] px-4 py-2 rounded-full text-sm font-semibold mb-5">
              <Hotel className="w-4 h-4" />
              {t('hotel_promo_badge')}
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4 leading-tight">
              {t('hotel_promo_title')}
            </h2>
            <p className="text-gray-600 text-lg mb-6">{t('hotel_promo_text')}</p>

            {/* Problem */}
            <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50/60 p-4 mb-6">
              <XCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">{t('hotel_promo_problem')}</p>
            </div>

            <ul className="space-y-3 mb-8">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[#0A0A0A] font-medium">{b}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                className="h-12 rounded-sm bg-[#FF3B30] hover:bg-[#E5352B] text-white font-bold px-6 shadow-lg shadow-[#FF3B30]/25"
                onClick={handleOrder}
                data-testid="hotel-promo-cta"
              >
                {t('hotel_promo_cta')} · €{t('hotel_price')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" className="h-12 rounded-sm border-2 border-[#0A0A0A] font-bold px-6" onClick={scrollToPricing}>
                🇹🇳 {t('hotel_price_tnd')} TND · DHMAD.tn
              </Button>
            </div>
          </motion.div>

          {/* Visual: Beispiel-Bestätigung */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="relative mx-auto w-full max-w-md"
          >
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#FF3B30]/15 via-transparent to-[#0A0A0A]/10 blur-2xl" aria-hidden="true" />
            <div className="relative rounded-2xl bg-white border border-gray-200 shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between bg-[#0A0A0A] px-5 py-4 text-white">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5 text-green-400" />
                  <span className="font-bold">{t('hotel_mock_confirmed')}</span>
                </div>
                <span className="text-xs text-white/60">PDF</span>
              </div>
              <div className="p-5 sm:pb-10 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Hotel className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-[#0A0A0A]">Hôtel Exemple ★★★</p>
                    <p className="text-sm text-gray-500">Paris, France</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-gray-50 p-3">
                    <p className="text-gray-500 text-xs">{t('hotel_check_in')}</p>
                    <p className="font-semibold text-[#0A0A0A]">12/06</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <p className="text-gray-500 text-xs">{t('hotel_check_out')}</p>
                    <p className="font-semibold text-[#0A0A0A]">19/06</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3 col-span-2">
                    <p className="text-gray-500 text-xs">{t('hotel_mock_guests')}</p>
                    <p className="font-semibold text-[#0A0A0A]">M. Ali B. · Mme Sarra B.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                  {t('hotel_mock_free_cancel')}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="w-3.5 h-3.5" />
                  {t('hotel_mock_verifiable')}
                </div>
              </div>
            </div>
            <div className="absolute -bottom-5 -left-4 hidden sm:flex items-center gap-2 rounded-full bg-white border border-gray-200 shadow-lg px-4 py-2 text-sm font-semibold text-[#0A0A0A]">
              <CalendarCheck className="w-4 h-4 text-[#FF3B30]" />
              {t('hotel_mock_ready')}
            </div>
          </motion.div>
        </div>

        {/* So funktioniert's */}
        <div className="mt-20">
          <h3 className="font-heading text-2xl font-bold text-[#0A0A0A] text-center mb-10">{t('hotel_steps_title')}</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map(({ icon: Icon, title, desc }, idx) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative rounded-xl border border-gray-200 bg-[#F9FAFB] p-6"
              >
                <span className="absolute top-4 right-5 font-heading text-4xl font-extrabold text-gray-200">{idx + 1}</span>
                <div className="w-12 h-12 rounded-full bg-[#FF3B30] flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-heading font-bold text-lg text-[#0A0A0A] mb-2">{title}</h4>
                <p className="text-gray-600 text-sm">{desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-gray-500 max-w-3xl mx-auto">{t('hotel_promo_disclaimer')}</p>
        </div>
      </div>
    </section>
  );
};

export default HotelPromoSection;
